import { useEffect, useRef } from "react";
import type { ChangeSetFileContent } from "@racler/protocol";
import { monaco, languageForPath } from "../lib/monaco.ts";
import { diffSides } from "../lib/review.ts";

/** Read-only Monaco diff editor for one file of a proposal (create: empty → new, delete: old → empty). */
export function ReviewDiff({ file }: { file: ChangeSetFileContent | undefined }) {
  const host = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!host.current || !file) return;
    const side = diffSides(file);
    const lang = languageForPath(file.path);
    const original = monaco.editor.createModel(side.original, lang);
    const modified = monaco.editor.createModel(side.modified, lang);
    const editor = monaco.editor.createDiffEditor(host.current, {
      theme: "racler-dark",
      automaticLayout: true,
      readOnly: true,
      originalEditable: false,
      renderSideBySide: true,
      useInlineViewWhenSpaceIsLimited: true,
      minimap: { enabled: false },
      scrollBeyondLastLine: false,
      fontSize: 13,
    });
    editor.setModel({ original, modified });
    return () => {
      editor.dispose();
      original.dispose();
      modified.dispose();
    };
  }, [file]);
  return <div className="review-diff" ref={host} data-testid="review-diff" />;
}
